/**
 * Valeurs et règles de saisie de la fiche de recueil du besoin employeur.
 *
 * Séparées du composant : le rendu et la mise en forme des valeurs envoyées à
 * l'API n'ont pas à vivre dans le même module.
 */

import type { Besoin, LigneOffre, TypeContratBesoin } from './useEntreprises';

export const CLASSE_CHAMP =
  'w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm';

/** Faisabilité : la fiche coche oui ou non, et peut rester sans réponse. */
export type Faisabilite = '' | 'oui' | 'non';

/**
 * État de saisie de la fiche. Tout est gardé en chaînes, comme les champs du
 * DOM : la conversion n'a lieu qu'à l'envoi, une seule fois et au même endroit.
 */
export interface ValeursFiche {
  postes: string;
  formationCode: string;
  dateSouhaitee: string;
  profilRecherche: string;
  rqth: boolean;
  dateDemande: string;
  commercial: string;
  typeContrat: '' | TypeContratBesoin;
  taches: LigneOffre[];
  faisabilite: Faisabilite;
  motifFaisabilite: string;
  candidatNom: string;
  candidatTelephone: string;
  candidatEmail: string;
}

/** Date du jour à La Réunion, au format attendu par un `<input type="date">`. */
function aujourdHuiReunion(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Indian/Reunion' });
}

function jourSaisissable(valeur: string | null): string {
  if (!valeur) return '';
  return new Date(valeur).toLocaleDateString('en-CA', { timeZone: 'Indian/Reunion' });
}

/** Trois lignes vides : la fiche papier en propose trois, et on en ajoute. */
const LIGNES_OFFRE_INITIALES: LigneOffre[] = [
  { rncp: '', taches: '' },
  { rncp: '', taches: '' },
  { rncp: '', taches: '' },
];

/**
 * Valeurs de départ. Sans besoin, c'est un recueil : la date de la demande part
 * du jour et le commercial du compte connecté, deux saisies que la personne
 * referait à l'identique — corrigeables, toutes les deux.
 */
export function valeursInitiales(
  besoin: Besoin | null,
  commercialParDefaut: string,
): ValeursFiche {
  if (!besoin) {
    return {
      postes: '1',
      formationCode: '',
      dateSouhaitee: '',
      profilRecherche: '',
      rqth: false,
      dateDemande: aujourdHuiReunion(),
      commercial: commercialParDefaut,
      typeContrat: '',
      taches: LIGNES_OFFRE_INITIALES,
      faisabilite: '',
      motifFaisabilite: '',
      candidatNom: '',
      candidatTelephone: '',
      candidatEmail: '',
    };
  }
  return {
    postes: String(besoin.nombrePostes),
    formationCode: besoin.formationCode ?? '',
    dateSouhaitee: jourSaisissable(besoin.dateSouhaitee),
    profilRecherche: besoin.profilRecherche ?? '',
    rqth: besoin.ouvertRqth,
    dateDemande: jourSaisissable(besoin.dateDemande),
    commercial: besoin.commercial ?? '',
    typeContrat: besoin.typeContrat ?? '',
    taches: besoin.tachesOffre.length > 0 ? besoin.tachesOffre : LIGNES_OFFRE_INITIALES,
    faisabilite: besoin.faisabilite === null ? '' : besoin.faisabilite ? 'oui' : 'non',
    motifFaisabilite: besoin.motifFaisabilite ?? '',
    candidatNom: besoin.candidatNom ?? '',
    candidatTelephone: besoin.candidatTelephone ?? '',
    candidatEmail: besoin.candidatEmail ?? '',
  };
}

/** Lignes réellement remplies : une ligne vide n'est pas envoyée. */
export function tachesRemplies(taches: LigneOffre[]): LigneOffre[] {
  return taches
    .map((ligne) => ({ rncp: ligne.rncp.trim(), taches: ligne.taches.trim() }))
    .filter((ligne) => ligne.rncp.length > 0 || ligne.taches.length > 0);
}

const postesEntiers = (valeur: string): number => parseInt(valeur, 10);

export function postesValides(valeur: string): boolean {
  const nombre = postesEntiers(valeur);
  return Number.isInteger(nombre) && nombre >= 1 && nombre <= 200;
}

export function nombrePostes(valeur: string): number {
  return postesEntiers(valeur);
}

/**
 * Un e-mail saisi doit être plausible : l'API le refuse sinon, et l'erreur
 * arriverait après l'envoi de toute la fiche.
 */
export function emailValide(valeur: string): boolean {
  const texte = valeur.trim();
  return texte.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(texte);
}
